'use client'

import { useEffect, useState } from 'react'
import { isPdfBackedAttachment } from '@/utils/nativePdfRendererUtils'
import type { Attachment } from '@/utils/types'
import InvestorRelationsDesktopDocumentViewer from './InvestorRelationsDesktopDocumentViewer'
import InvestorRelationsMobileDocumentViewer from './InvestorRelationsMobileDocumentViewer'

const MOBILE_DOCUMENT_VIEWER_QUERY = '(max-width: 1024px)'

interface InvestorRelationsDocumentViewerProps {
  attachment: Attachment | null
  isOpen: boolean
  onClose: () => void
  companySlug: string
  section: string
  anonymousMode?: boolean
  preDownloadedBlobUrl?: string | null
  autoPlay?: boolean
  isPreview?: boolean
  organisationId?: string
  companyLogoUrl?: string | null
  assiduousIconUrl?: string
}

const useMobileDocumentViewport = () => {
  const [viewportState, setViewportState] = useState({
    isMobile: false,
    hasResolved: false,
  })

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_DOCUMENT_VIEWER_QUERY)
    const updateViewportState = () => {
      setViewportState({
        isMobile: mediaQuery.matches,
        hasResolved: true,
      })
    }

    updateViewportState()
    mediaQuery.addEventListener('change', updateViewportState)

    return () => {
      mediaQuery.removeEventListener('change', updateViewportState)
    }
  }, [])

  return viewportState
}

const InvestorRelationsDocumentViewer = ({
  attachment,
  isOpen,
  onClose,
  companySlug,
  section,
  anonymousMode = false,
  preDownloadedBlobUrl = null,
  autoPlay = false,
  isPreview = false,
  organisationId = undefined,
  companyLogoUrl = null,
  assiduousIconUrl = '/images/AssiduousIcon.svg',
}: InvestorRelationsDocumentViewerProps) => {
  const { isMobile, hasResolved } = useMobileDocumentViewport()
  const canUseMobileNativeViewer =
    hasResolved && isMobile && isPdfBackedAttachment(attachment)
  const shouldWaitForMobileViewport =
    isOpen && !hasResolved && isPdfBackedAttachment(attachment)

  if (shouldWaitForMobileViewport) {
    return null
  }

  if (canUseMobileNativeViewer) {
    return (
      <InvestorRelationsMobileDocumentViewer
        attachment={attachment}
        isOpen={isOpen}
        onClose={onClose}
        companySlug={companySlug}
        section={section}
        isPreview={isPreview}
        organisationId={organisationId}
        preDownloadedBlobUrl={preDownloadedBlobUrl}
        assiduousIconUrl={assiduousIconUrl}
      />
    )
  }

  return (
    <InvestorRelationsDesktopDocumentViewer
      attachment={attachment}
      isOpen={isOpen}
      onClose={onClose}
      anonymousMode={anonymousMode}
      companySlug={companySlug}
      section={section}
      preDownloadedBlobUrl={preDownloadedBlobUrl}
      autoPlay={autoPlay}
      isPreview={isPreview}
      organisationId={organisationId}
      companyLogoUrl={companyLogoUrl}
      assiduousIconUrl={assiduousIconUrl}
    />
  )
}

export default InvestorRelationsDocumentViewer
